import { FormControl, MenuItem, Select, type SelectChangeEvent, Switch } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";

import {
  type NotificationCellState,
  type NotificationChangeArguments,
  type SelectorValue,
} from "./types";

const SELECT_MIN_WIDTH = 72;

interface NotificationSelectorDropdownProps {
  cell: NotificationCellState;
  onChange: (arguments_: NotificationChangeArguments) => void;
  onOpen: () => void;
}

export function NotificationSelectorDropdown(props: NotificationSelectorDropdownProps) {
  const { cell, onChange, onOpen } = props;

  if (cell.kind === "blank") {
    return (
      <Text
        inline
        variant="body2"
        sx={(theme) => ({ color: theme.palette.text?.secondary })}
        data-testid="notification-blank"
      >
        —
      </Text>
    );
  }

  if (cell.kind === "uneditable") {
    return (
      <Switch
        disabled
        checked={cell.text === "On"}
        data-testid="uneditable-notification-option"
        inputProps={{ "aria-label": `${cell.text} (locked)` }}
      />
    );
  }

  if (cell.kind === "switch") {
    const handleSwitchChange = (_: unknown, checked: boolean) => {
      onChange({
        alertType: cell.alertType,
        value: checked ? "on" : "off",
        notificationKey: cell.notificationKey,
      });
    };

    return (
      <Switch
        checked={cell.isOn}
        data-testid="notification-container"
        disabled={cell.isDisabled}
        inputProps={{ "aria-label": `${cell.alertType} notification preference` }}
        onChange={handleSwitchChange}
      />
    );
  }

  const handleSelectChange = (event: SelectChangeEvent<SelectorValue>) => {
    onChange({
      alertType: cell.alertType,
      value: event.target.value as SelectorValue,
      notificationKey: cell.notificationKey,
    });
  };

  return (
    <FormControl size="small" data-testid="notification-container">
      <Select<SelectorValue>
        value={cell.value}
        disabled={cell.isDisabled}
        inputProps={{ "aria-label": `${cell.alertType} notification preference` }}
        sx={{ minWidth: SELECT_MIN_WIDTH }}
        onChange={handleSelectChange}
        onOpen={onOpen}
      >
        {cell.options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            data-testid={option.label}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
